/**
 * LinkedInContentPage — /linkedin-presence/content
 *
 * Post archetypes with real exemplar posts + weekly content calendar
 * generator. News Commentary archetype is free; others All Access.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  POST_ARCHETYPES,
  CONTENT_PROMPTS,
  type PostArchetype,
  type LinkedInPrompt,
} from "../data/linkedinPresenceContent";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";
import PostGeneratorCard from "../components/linkedin/PostGeneratorCard";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

function ArchetypeCard({ archetype, canAccess, isPro }: { archetype: PostArchetype; canAccess: boolean; isPro: boolean }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(archetype.exemplar.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const unlocked = canAccess || archetype.tier === "free";

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 4 }} id={archetype.slug}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={500} gutterBottom>{archetype.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620, lineHeight: 1.6 }}>{archetype.purpose}</Typography>
          <Stack direction="row" spacing={0.75} mt={1.5} flexWrap="wrap" gap={0.5}>
            <Chip label={archetype.idealFrequency} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
            {archetype.tier === "free" ? (
              <Chip label={t("LinkedInContentPage.freeChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark, fontWeight: 500, fontSize: "0.68rem", height: 20 }} />
            ) : (
              <Chip icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />} label={t("LinkedInContentPage.allAccessChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 500, fontSize: "0.68rem", height: 20 }} />
            )}
          </Stack>
        </Box>
      </Stack>

      {unlocked ? (
        <>
          {/* Structure */}
          <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 2, p: 2.5, mb: 3 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 1.5 }}>
              {t("LinkedInContentPage.theStructureLabel")}
            </Typography>
            <Stack spacing={0.75}>
              {archetype.structure.map((s, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Typography variant="caption" color="text.disabled" fontWeight={500} sx={{ minWidth: 20, mt: "2px" }}>{i + 1}.</Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{s}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          {/* Hook formulas */}
          <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`, borderRadius: 2, p: 2.5, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <LightbulbIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInContentPage.hookFormulasLabel")}</Typography>
            </Stack>
            <Stack spacing={0.75}>
              {archetype.hookFormulas.map((h, i) => (
                <Typography key={i} variant="body2" sx={{ lineHeight: 1.55, fontStyle: "italic", color: "text.primary" }}>"{h}"</Typography>
              ))}
            </Stack>
          </Paper>

          {/* Exemplar post */}
          <Paper elevation={0} sx={{ bgcolor: "background.paper", border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`, borderRadius: 3, p: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormatQuoteIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Typography variant="caption" fontWeight={800} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  {t("LinkedInContentPage.exemplarPostLabel")}
                </Typography>
              </Stack>
              <Tooltip title={copied ? t("LinkedInContentPage.copiedTooltip") : t("LinkedInContentPage.copyPostTextTooltip")}>
                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? theme.palette.success.dark : theme.palette.primary.main }}>
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif", color: "text.primary" }}>
              {archetype.exemplar.body}
            </Typography>
          </Paper>

          {/* Why it works */}
          <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, borderRadius: 2, p: 2.5 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.info.dark, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 1.5 }}>
              {t("LinkedInContentPage.whyThisPostWorksLabel")}
            </Typography>
            <Stack spacing={1}>
              {archetype.exemplar.whyItWorks.map((w, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Typography variant="body2" color={theme.palette.info.dark} fontWeight={500}>{i + 1}.</Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, flex: 1 }}>{w}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </>
      ) : isPro ? <ProUpsellNote /> : (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 2, p: 4, textAlign: "center" }}>
          <LockIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5), mb: 2 }} />
          <Typography variant="h6" fontWeight={500} gutterBottom>{t("LinkedInContentPage.allAccessArchetypeTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: "auto" }}>
            {t("LinkedInContentPage.allAccessArchetypeBody")}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
            {t("LinkedInContentPage.seeAllAccessPricingButton")}
          </Button>
        </Paper>
      )}
    </Paper>
  );
}

function CalendarPromptCard({ prompt, canAccess, isPro }: { prompt: LinkedInPrompt; canAccess: boolean; isPro: boolean }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt.promptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const unlocked = canAccess || prompt.tier === "free";

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3 }}>
      <Typography variant="h6" fontWeight={500} gutterBottom>{prompt.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>{prompt.tagline}</Typography>

      {prompt.whatItDoes.map((para, i) => (
        <Typography key={i} variant="body2" sx={{ mb: 1.25, lineHeight: 1.75 }}>{para}</Typography>
      ))}

      {unlocked ? (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 2, p: 2.5, mt: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInContentPage.thePromptLabel")}</Typography>
            <Tooltip title={copied ? t("LinkedInContentPage.copiedTooltip") : t("LinkedInContentPage.copyToClipboardTooltip")}>
              <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? theme.palette.success.dark : theme.palette.primary.main }}>
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          <Box component="pre" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.78rem", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, maxHeight: 500, overflowY: "auto", p: 1 }}>
            {prompt.promptText}
          </Box>
        </Paper>
      ) : isPro ? <ProUpsellNote /> : (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 2, p: 3, textAlign: "center", mt: 2.5 }}>
          <LockIcon sx={{ fontSize: 32, color: alpha(theme.palette.primary.main, 0.5), mb: 1.5 }} />
          <Typography variant="body1" fontWeight={500} gutterBottom>{t("LinkedInContentPage.unlockWithAllAccessMessage")}</Typography>
          <Button variant="contained" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
            {t("LinkedInContentPage.seeAllAccessPricingButton")}
          </Button>
        </Paper>
      )}
    </Paper>
  );
}

export default function LinkedInContentPage() {
  const { t, i18n } = useTranslation("pages-products");
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const tierAccess = getTierAccess(user);
  const canAccess = tierAccess.canAccessProducts;
  const postArchetypes = L(POST_ARCHETYPES, i18n.language);
  const contentPrompts = L(CONTENT_PROMPTS, i18n.language);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/linkedin-presence")} size="small" sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}>{t("LinkedInContentPage.breadcrumbLinkedInPresence")}</Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("LinkedInContentPage.breadcrumbContentStrategy")}</Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1 }}>{t("LinkedInContentPage.pageTitle")}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65 }}>
            {t("LinkedInContentPage.pageIntro")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {!canAccess && (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Alert severity="info" icon={<LockIcon />} sx={{ mb: 4, borderRadius: 2 }} action={<Button size="small" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />}>{t("LinkedInContentPage.seeAllAccessButton")}</Button>}>
              <Trans
                i18nKey="LinkedInContentPage.gateNotice"
                t={t}
                components={[<strong key="strong0" />]}
              />
            </Alert>
          )
        )}

        {/* AI Post Generator — All-Access (D4). Topic + POV → three distinct
            variants. Gate is UX only; the API is the enforcement point. */}
        <Typography variant="h5" fontWeight={500} sx={{ mb: 1, letterSpacing: "-0.02em" }}>
          {t("LinkedInContentPage.postGeneratorHeading")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("LinkedInContentPage.postGeneratorSubheading")}
        </Typography>
        <PostGeneratorCard user={user} />

        <Divider sx={{ my: 5 }} />

        <Typography variant="h5" fontWeight={500} sx={{ mb: 3, letterSpacing: "-0.02em" }}>{t("LinkedInContentPage.postArchetypesHeading")}</Typography>
        {postArchetypes.map((a) => <ArchetypeCard key={a.slug} archetype={a} canAccess={canAccess} isPro={tierAccess.isPro} />)}

        <Divider sx={{ my: 5 }} />

        <Typography variant="h5" fontWeight={500} sx={{ mb: 3, letterSpacing: "-0.02em" }}>{t("LinkedInContentPage.weeklyOperatingRhythmHeading")}</Typography>
        {contentPrompts.map((p) => <CalendarPromptCard key={p.slug} prompt={p} canAccess={canAccess} isPro={tierAccess.isPro} />)}

        <Paper elevation={0} sx={{ mt: 5, p: 4, textAlign: "center", bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={500} gutterBottom>{t("LinkedInContentPage.footerCtaTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: "auto" }}>
            {t("LinkedInContentPage.footerCtaBody")}
          </Typography>
          <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/linkedin-presence/outreach")} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
            {t("LinkedInContentPage.seeOutreachTemplatesButton")}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
